import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import CloseIcon from '@mui/icons-material/Close';

const DISMISS_KEY = 'thinkstyne_pwa_install_prompt_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isMobileDevice = () =>
  window.matchMedia?.('(max-width: 768px)').matches ||
  /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent || '');

const isIos = () => /iPhone|iPad|iPod/i.test(window.navigator.userAgent || '');

function wasRecentlyDismissed() {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures; prompt can still be dismissed for this render.
  }
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [installed, setInstalled] = useState(false);

  const canShow = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return isMobileDevice() && !isStandalone() && !wasRecentlyDismissed();
  }, []);

  useEffect(() => {
    if (!canShow) return undefined;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setTimeout(() => setOpen(true), 1200);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setOpen(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    if (isIos()) {
      const timer = setTimeout(() => setOpen(true), 1800);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [canShow]);

  const handleClose = () => {
    markDismissed();
    setOpen(false);
    setIosHelpOpen(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice?.outcome !== 'accepted') markDismissed();
      setOpen(false);
      return;
    }

    if (isIos()) {
      setIosHelpOpen(true);
      return;
    }

    markDismissed();
    setOpen(false);
  };

  return (
    <>
      <Snackbar
        open={open && !iosHelpOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ px: 1, bottom: { xs: 16, sm: 24 } }}
      >
        <Alert
          severity="info"
          icon={<AddToHomeScreenIcon />}
          action={
            <Stack direction="row" spacing={0.5}>
              <Button color="inherit" size="small" onClick={handleInstall} sx={{ whiteSpace: 'nowrap' }}>
                Save App
              </Button>
              <IconButton size="small" color="inherit" onClick={handleClose} aria-label="Dismiss app install prompt">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
          sx={{
            width: 'min(560px, calc(100vw - 24px))',
            alignItems: 'center',
            boxShadow: 4,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Save ThinkStyne to your phone?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Open it faster from your home screen.
          </Typography>
        </Alert>
      </Snackbar>

      <Dialog open={iosHelpOpen} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddToHomeScreenIcon color="primary" />
          Save ThinkStyne
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              On iPhone or iPad, tap the browser Share button, then choose <strong>Add to Home Screen</strong>.
            </Typography>
            <Typography variant="body2">
              ThinkStyne will open like an app from your home screen.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Maybe later</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={installed}
        autoHideDuration={3500}
        onClose={() => setInstalled(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setInstalled(false)}>
          ThinkStyne was added to your device.
        </Alert>
      </Snackbar>
    </>
  );
}
